import {TagColor} from './tags.js';
import {DateTime} from 'luxon';

export type ID = string;

export interface Customer {
	id: ID;
	name: string;
	email: string | null;
	phoneNumber: string | null;
	notes: string | null;
	address: string | null;
}

export interface Invoice {
	id: ID;
	publicId: number;
	date: DateTime<true>;
	description: string;
	customer: ID;
	// Embedded because they don't exist outside of this context
	items: InvoiceLineItem[];
}

export interface InvoiceLineItem {
	id: ID;
	type: InvoiceLineItemType;
	name: string;
	description: string | null;
	quantity: number | null;
	price: number;
	taxed: boolean | null;
}

export enum InvoiceLineItemType {
	Cost = 'COST',
	Credit = 'CREDIT',
	Payment = 'PAYMENT',
}

export interface Project {
	id: ID;
	name: string;
	description: string;
	startDate: DateTime;
	endDate: DateTime | null;
	hoursLogged: number | null;
	cost: number | null;
	deposit: number | null;
	status: ProjectStatus;
	invoice: ID | null;
	customer: ID;
	transactions: ID[];
	tags: ID[];
	files: ID[];
	notes: ID[];
}

export enum ProjectStatus {
	Cancelled = 'CANCELLED',
	Done = 'DONE',
	FollowUp = 'FOLLOW_UP',
	InProgress = 'IN_PROGRESS',
	Planning = 'PLANNING',
	Upcoming = 'UPCOMING',
}

export interface Transaction {
	id: ID;
	date: DateTime;

	payee: string | null;
	memo: string;
	amount: number;

	paymentAccount: ID;
	category: ID;
	tags: ID[];
	projects: ID[];
	sale: ID | null;
}

export interface TransactionFilters {
	startDate?: DateTime;
	endDate?: DateTime;
	categoryIds?: ID[];
	tagIds?: ID[];
	excludedTagIds?: ID[];
	projectIds?: ID[];
	includeDeleted?: boolean;
}

export interface Tag {
	id: ID;
	name: string;
	color: TagColor;
	internalId: string | null;
	parentTagId: ID | null;
}

export type PaymentAccountType = 'BANK' | 'CREDIT' | 'CASH';

export interface PaymentAccount {
	id: ID;
	type: PaymentAccountType;
	name: string;
	closed: boolean;
	balance: number;
}

export interface CategoryGroup {
	id: ID;
	name: string;
	priority: number;
	hidden: boolean;
	// Embedded because groups without categories aren't useful
	categories: Category[];
}

export interface Category {
	id: ID;
	name: string;
	priority: number;
	hidden: boolean;
	// Embedded for ease of use
	categoryGroup: Omit<CategoryGroup, 'categories'>;
}

export interface File {
	id: ID;
	name: string;
	description: string | null;
	mimeType: string;
	size: number;
}

export interface Product {
	id: ID;
	name: string;
	// Embedded because ProductVariant objects don't exist outside of this context
	variants: ProductVariant[];
}

export interface ProductVariant {
	id: ID;
	description: string;
}

export interface Note {
	id: ID;
	name: string;
	// Don't want to import Quill just for the type
	content: any;
}

export interface Trip {
	id: ID;
	tripDate: DateTime;
	mileage: number;
	destination: string;
	memo: string | null;
}

export interface Sale {
	id: ID;
	note: string | null;
	transaction: string;
	// Embedded because SaleItem objects don't exist outside of this context
	items: SaleItem[];
}

export interface SaleItem {
	id: ID;
	receivedAmount: number;
	faceValue: number | null;
	taxAmount: number | null;
	quantity: number | null;
	note: string | null;
	product: string | null;
	productVariant: string | null;
}
